// frontend/src/pages/Estimates.jsx
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import React, {useEffect, useState} from "react";
import {Paper, Menu, MenuItem} from "@mui/material";
import {DataGrid} from "@mui/x-data-grid";
import {api} from "../../services/apiService";
import {formatDateOnlyISO} from "../../utils/helpers";
import {API_BASE_URL} from "../../config";

export default function Estimates() {
    const [estimates, setEstimates] = useState([]);
    const [datagridLoading, setDatagridLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [totalEstimates, setTotalEstimates] = useState(0);
    const [query, setQuery] = useState("");

    // menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);

    const columns = [
        {field: "ref", headerName: "N° Devis", flex: 1, minWidth: 120},
        {field: "booking_ref", headerName: "N° Réservation", flex: 1, minWidth: 120},
        {field: "client", headerName: "Client", flex: 1.2, minWidth: 180, valueGetter: params => params.name},
        {
            field: "created_at",
            headerName: "Date de création",
            flex: 1,
            minWidth: 100,
            valueGetter: params => formatDateOnlyISO(params)
        },
        {
            field: "due_date",
            headerName: "Date d'expiration",
            flex: 1,
            minWidth: 120,
            valueGetter: params => formatDateOnlyISO(params)
        },
        {
            field: "total_ttc",
            headerName: "Montant TTC (€)",
            flex: 1,
            minWidth: 120,
            valueFormatter: (params) =>
                params.toLocaleString("fr-FR", {
                    style: "currency",
                    currency: "EUR",
                }),
        }
    ];

    useEffect(() => {
        const fetchData = async () => {
            setDatagridLoading(true);
            api.get(`/estimates/list?page=${page + 1}&per_page=${perPage}${query ? `&query=${encodeURIComponent(query)}` : ""}`)
                .then((response) => {
                    setEstimates(response.data.items);
                    setTotalEstimates(response.data.total);
                    setDatagridLoading(false);
                });
        };

        const timeout = setTimeout(() => {
            void fetchData();
        }, 200);

        return () => clearTimeout(timeout);
    }, [page, perPage, query]);

    const handleRowClick = (event, row) => {
        setSelectedRow(row);
        setAnchorEl(event.currentTarget || event.target);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSelectedRow(null);
    };

    const openEstimate = () => {
        if (!selectedRow) return handleClose();
        const estimateId = selectedRow.id;
        const pdfUrl = `${API_BASE_URL}/estimates/get_estimate/${estimateId}`;
        window.open(pdfUrl, "_blank");
        handleClose();
    };

    const openBooking = () => {
        if (!selectedRow) return handleClose();
        const bookingId = selectedRow.booking_id;
        const bookingRef = selectedRow.booking_ref;
        if (bookingId) {
            window.open(`/bookings/details/${bookingId}`, "_blank");
        } else if (bookingRef) {
            window.open(`/bookings/${bookingRef}`, "_blank");
        }
        handleClose();
    };

    return (
        <Container sx={{overflowX: "auto", width: "100%"}}>
            <Typography variant="h1" sx={{mb: 3}}>Devis</Typography>

            <Grid container spacing={2} sx={{display: 'flex', justifyContent: 'space-between', mt: '2rem'}}>
                <TextField
                    sx={{width: {xs: "100%", md: "auto"}}}
                    placeholder="Rechercher un devis..."
                    variant="outlined"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon/>
                            </InputAdornment>
                        ),
                    }}
                    onChange={e => setQuery(e.target.value)}
                    value={query}
                />
            </Grid>

            <Paper sx={{height: 400, width: "100%", mt: "2rem"}}>
                <DataGrid
                    rows={estimates}
                    columns={columns}
                    paginationModel={{page: page, pageSize: perPage}}
                    onPaginationModelChange={(model) => {
                        setPage(model.page);
                        setPerPage(model.pageSize);
                    }}
                    loading={datagridLoading}
                    pageSizeOptions={[10, 25, 50]}
                    rowCount={totalEstimates}
                    paginationMode="server"
                    onRowClick={(params, event) => handleRowClick(event, params.row)}
                    sx={{
                        border: 0,
                        cursor: "pointer",
                        "& .MuiDataGrid-row:hover": {
                            backgroundColor: "#f0f0f0",
                        },
                        backgroundColor: "#fff",
                    }}
                />
            </Paper>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <MenuItem onClick={openEstimate}>Ouvrir le devis</MenuItem>
                <MenuItem onClick={openBooking} disabled={!(selectedRow && (selectedRow.booking_id || selectedRow.booking_ref))}>
                    Ouvrir la réservation
                </MenuItem>
            </Menu>
        </Container>
    );
}
